import React, { useState } from 'react';
import { Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';

import { MasterConfigs } from '../../../../../../interfaces/entity';
import ReusableTable from '../../../../../../components/TableComponent';

interface DealRecord {
  _id: string;
  dealName: string;
  assignedDate: string;
  client: string;
  engagementTime: string;
  totalDealValue: number;
  revenueReceived: number;
  profit: number;
  margin: number;
  developerCost: number;
  paymentDate: string;
  leadType: string;
  stage: 'Completed' | 'In Progress' | 'On Hold';
}

const DealFinancialTable: React.FC = () => {
  const [search, setSearch] = useState('');

  const tableConfig: MasterConfigs = {
    api: () => '/deal-financial-summary',
    loadPagination: true,
    queryParams: { search },

    renderComponent: () => (
      <div className="flex justify-between items-center mb-4 px-2">
        <h3 className="text-base font-semibold text-gray-700">Deal Financial Summary</h3>
        <Input
          size="middle"
          placeholder="Search"
          prefix={<SearchOutlined className="text-gray-400" />}
          className="w-64 rounded-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
    ),

    getColumns: (): ColumnsType<DealRecord> => [
      {
        title: 'Deals Name',
        dataIndex: 'dealName',
        key: 'dealName',
        render: (text) => <span className="text-gray-600 font-medium whitespace-nowrap">{text || 'Deal Name'}</span>,
      },
      {
        title: 'Assigned Date',
        dataIndex: 'assignedDate',
        render: (date) => <span className="whitespace-nowrap">{date}</span>,
      },
      {
        title: 'Client',
        dataIndex: 'client',
        render: (text) => <span className="whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Engagement Time',
        dataIndex: 'engagementTime',
        align: 'center',
      },
      {
        title: 'Total Deal Value',
        dataIndex: 'totalDealValue',
        render: (v) => <span className="whitespace-nowrap">₹{v?.toLocaleString()}</span>,
      },
      {
        title: 'Revenue Received',
        dataIndex: 'revenueReceived',
        render: (v) => <span className="whitespace-nowrap">{v ? `₹${v.toLocaleString()}` : '-'}</span>,
      },
      {
        title: 'Profit',
        dataIndex: 'profit',
        render: (v) => <span className="whitespace-nowrap">₹{v?.toLocaleString()}</span>,
      },
      {
        title: 'Margin %',
        dataIndex: 'margin',
        render: (v) => `${v}%`,
      },
      {
        title: 'Developer Deployment cost',
        dataIndex: 'developerCost',
        render: (v) => <span className="whitespace-nowrap">₹{v?.toLocaleString()}</span>,
      },
      {
        title: 'Payment Date',
        dataIndex: 'paymentDate',
        render: (date) => <span className="whitespace-nowrap">{date || '-'}</span>,
      },
      {
        title: 'Lead type',
        dataIndex: 'leadType',
      },
      {
        title: 'Stage',
        dataIndex: 'stage',
        render: (stage) => (
          <span className={stage === 'Completed' ? 'text-green-600' : 'text-blue-500 font-medium'}>
            {stage}
          </span>
        ),
      },
      {
        title: 'Timeline',
        align: 'center',
        render: () => (
          <span className="text-blue-500 cursor-pointer hover:underline text-sm font-medium">
            View
          </span>
        ),
      },
    ],

    tableProps: {
      bordered: false,
      size: 'middle',
      // This ensures the table expands to its full width naturally
      // and only scrolls if it hits the right edge of the screen.
      scroll: { x: 'max-content' }, 
      pagination: {
        size: 'small',
        showSizeChanger: false,
      }
    },
  };

  return (
    <div className="bg-white p-4 rounded-lg pb-[50px]  mt-6">
      <ReusableTable<DealRecord> config={tableConfig} />
    </div>
  );
};

export default DealFinancialTable;